import type {
	CoursesAdminBulkSetCourseTagsDto,
	CoursesAdminExplainCourseAccessDto,
	CoursesAdminListAccessDecisionsQueryDto,
	CoursesAdminListAccessUsersQueryDto,
	CoursesAdminListCourseTagAuditQueryDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	AdminBulkSetCourseTagsInput,
	AdminBulkSetCourseTagsOutput,
	AdminListCourseTagAuditInput,
	AdminListCourseTagAuditOutput,
	AdminListAccessDecisionsInput,
	AdminListAccessDecisionsOutput,
	AdminExplainCourseAccessInput,
	AdminExplainCourseAccessOutput,
	AdminListAccessUsersInput,
	AdminListAccessUsersOutput,
} from '../../types/admin-course-access.types';

const TAG = 'courses:admin-course-access' as const;
const INVALIDATES = [TAG, 'courses:courses', 'courses:dashboard'] as const;

export const adminCourseAccessEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		adminBulkSetCourseTags: builder.mutation<
			AdminBulkSetCourseTagsOutput,
			AdminBulkSetCourseTagsInput
		>({
			query: (body) => ({
				url: 'courses/admin/courses/tags/bulk',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminBulkSetCourseTagsDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminListCourseTagAudit: builder.query<
			AdminListCourseTagAuditOutput,
			AdminListCourseTagAuditInput
		>({
			query: (params) => ({
				url: 'courses/admin/courses/tag-audit',
				params: params satisfies Wire<CoursesAdminListCourseTagAuditQueryDto>,
			}),
			providesTags: [TAG],
		}),
		adminListAccessDecisions: builder.query<
			AdminListAccessDecisionsOutput,
			AdminListAccessDecisionsInput
		>({
			query: (params) => ({
				url: 'courses/admin/courses/access-decisions',
				params: params satisfies Wire<CoursesAdminListAccessDecisionsQueryDto>,
			}),
			providesTags: [TAG],
		}),
		// A read (POST body) despite its name: the access report renders it like a query.
		adminExplainCourseAccess: builder.query<
			AdminExplainCourseAccessOutput,
			AdminExplainCourseAccessInput
		>({
			query: (body) => ({
				url: 'courses/admin/courses/access-explain',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminExplainCourseAccessDto>,
			}),
			providesTags: [TAG],
		}),
		adminListAccessUsers: builder.query<AdminListAccessUsersOutput, AdminListAccessUsersInput>({
			query: (params) => ({
				url: 'courses/admin/courses/access-users',
				params: params satisfies Wire<CoursesAdminListAccessUsersQueryDto>,
			}),
			providesTags: [TAG],
		}),
	}),
});

export const {
	useAdminBulkSetCourseTagsMutation,
	useAdminListCourseTagAuditQuery,
	useLazyAdminListCourseTagAuditQuery,
	useAdminListAccessDecisionsQuery,
	useLazyAdminListAccessDecisionsQuery,
	useAdminExplainCourseAccessQuery,
	useLazyAdminExplainCourseAccessQuery,
	useAdminListAccessUsersQuery,
	useLazyAdminListAccessUsersQuery,
} = adminCourseAccessEndpoints;
